import type { ReactNode } from 'react'
import { Box, Stack, Typography } from '@mui/material'

interface Props {
  title: string
  subtitle?: string
  actions?: ReactNode
  /** Optional sticky toolbar rendered directly beneath the title row. */
  toolbar?: ReactNode
  children: ReactNode
  /** Constrain content width for long-form reading pages. */
  maxWidth?: number | false
  /**
   * When true (default), the content area is the scroll container —
   * the header + toolbar stay pinned. Set false for pages that want
   * the page itself to scroll (rare).
   */
  scrollContent?: boolean
}

export function PageShell({
  title, subtitle, actions, toolbar, children, maxWidth = false, scrollContent = true,
}: Props) {
  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Header (stays pinned) ── */}
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          px: { xs: 3, md: 5 },
          pt: { xs: 3, md: 4 },
          pb: { xs: 2, md: 2.5 },
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: { xs: 22, md: 26 },
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-0.02em',
              color: 'text.primary',
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                fontSize: 14,
                letterSpacing: '-0.005em',
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
        {actions && (
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ flexShrink: 0 }}>
            {actions}
          </Stack>
        )}
      </Box>

      {/* ── Optional sticky toolbar row ── */}
      {toolbar && (
        <Box
          sx={{
            flexShrink: 0,
            px: { xs: 3, md: 5 },
            pb: 2,
          }}
        >
          {toolbar}
        </Box>
      )}

      {/* ── Content (scrolls internally when scrollContent=true) ── */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: scrollContent ? 'auto' : 'hidden',
          px: { xs: 3, md: 5 },
          pt: toolbar ? 0.5 : 1.5,
          pb: scrollContent ? { xs: 4, md: 5 } : 0,
          maxWidth: maxWidth || undefined,
          mx: maxWidth ? 'auto' : undefined,
          width: maxWidth ? '100%' : undefined,
          // Hidden-until-hover scrollbar — keeps the page feeling calm
          scrollbarWidth: 'thin',
          scrollbarColor: 'transparent transparent',
          '&::-webkit-scrollbar': { width: 8, height: 8 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'transparent',
            borderRadius: 999,
            transition: 'background-color 160ms ease',
          },
          '&:hover::-webkit-scrollbar-thumb': {
            background: (t) =>
              t.palette.mode === 'light' ? 'rgba(0,0,0,0.14)' : 'rgba(255,255,255,0.14)',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  )
}
